import i18n from "../../i18n";
import React, { useEffect, useState } from "react";
import {
  Stack,
  Textarea,
  Button,
  Group,
  Checkbox,
  Divider,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import McaStepFieldsInput from "./McaStepFieldsInput";
import McaStepFileList from "./McaStepFileList";
import { earlierStepFiles } from "./mcaStepFiles";
import { isFileFieldType } from "../../data/mcaEntities";

const SectionTitle = ({ children }) => (
  <Stack spacing="xs">
    <Text size="xs" color="dimmed" weight={600}>
      {children}
    </Text>
    <Divider />
  </Stack>
);

const buildInitialStepFields = (stepDef, stepResult) => {
  if ((stepResult?.stepFields ?? []).length > 0) {
    return stepResult.stepFields;
  }
  return (stepDef?.stepFields ?? []).map((item) => ({
    label: item.label,
    cname: item.cname,
    inputType: item.inputType ?? "TEXT",
    required: !!item.required,
    value: isFileFieldType(item.inputType) ? [] : null,
  }));
};

const McaApprovalForm = ({
  request,
  process,
  onApprove,
  onReject,
  onTerminate,
  onUploadField,
  canUpload,
  loading,
  uploading,
}) => {
  const { i18n } = useTranslation();
  const [rejectReason, setRejectReason] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const sortedSteps = (process?.steps ?? [])
    .slice()
    .sort((a, b) => a.order - b.order);
  const stepDef = sortedSteps[request?.currentStep];
  const stepResult = request?.stepResults?.[request?.currentStep];
  const [checklist, setChecklist] = useState(stepResult?.checklist ?? []);
  const [stepFields, setStepFields] = useState(() =>
    buildInitialStepFields(stepDef, stepResult),
  );

  useEffect(() => {
    setStepFields((prev) =>
      prev.map((field, idx) => {
        const serverField = stepResult?.stepFields?.[idx];
        if (isFileFieldType(field.inputType) && serverField) {
          return { ...field, value: serverField.value ?? [] };
        }
        return field;
      }),
    );
  }, [stepResult?.stepFields]);

  const toggleCheck = (idx) => {
    setChecklist((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  const cancelPendingAction = () => {
    setPendingAction(null);
    setRejectReason("");
  };

  const handleApprove = async (routingActionIndex) => {
    try {
      await onApprove({
        stepFields,
        checklist,
        ...(routingActionIndex != null ? { routingActionIndex } : {}),
      });
      showNotification({
        title: "Approved",
        message: "Step approved",
        color: "green",
      });
    } catch (e) {
      showNotification({ title: "Error", message: e.message, color: "red" });
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showNotification({
        title: "Comment required",
        message: "Please provide a rejection reason",
        color: "red",
      });
      return;
    }
    try {
      await onReject({ comment: rejectReason, checklist });
      showNotification({
        title: "Rejected",
        message: "Request returned to maker",
        color: "orange",
      });
    } catch (e) {
      showNotification({ title: "Error", message: e.message, color: "red" });
    }
  };

  const handleTerminate = async () => {
    if (!rejectReason.trim()) {
      showNotification({
        title: "Reason required",
        message: "Please provide a termination reason",
        color: "red",
      });
      return;
    }
    try {
      await onTerminate({ reason: rejectReason });
      showNotification({
        title: "Terminated",
        message: "Request permanently closed",
        color: "red",
      });
    } catch (e) {
      showNotification({ title: "Error", message: e.message, color: "red" });
    }
  };

  const hasChecklist = (checklist ?? []).length > 0;
  const hasFormFields = (stepFields ?? []).length > 0;
  const filesToReview = earlierStepFiles(request);
  const routingActions = stepDef?.routingActions ?? [];
  const hasRoutingActions = routingActions.length > 0;
  const useChineseLabel =
    i18n.language?.startsWith("zh") || i18n.language === "ch";
  const routingActionLabel = (action) => {
    if (useChineseLabel && action.cname?.trim()) return action.cname.trim();
    return action.label?.trim() || "Action";
  };

  return (
    <Stack spacing="lg">
      {filesToReview.length > 0 && (
        <Stack spacing="sm" data-testid="mca-earlier-files">
          <SectionTitle>{i18n.t("Files from earlier steps")}</SectionTitle>
          <McaStepFileList files={filesToReview} />
        </Stack>
      )}

      {hasChecklist && (
        <Stack spacing="sm">
          <SectionTitle>{i18n.t("Check List Items")}</SectionTitle>
          {checklist.map((item, idx) => (
            <Checkbox
              key={idx}
              label={`${item.label}${item.required ? " *" : ""}`}
              checked={!!item.checked}
              onChange={() => toggleCheck(idx)}
            />
          ))}
        </Stack>
      )}

      {hasFormFields && (
        <Stack spacing="sm">
          <SectionTitle>{i18n.t("Form")}</SectionTitle>
          <McaStepFieldsInput
            fields={stepFields}
            onChange={setStepFields}
            canUpload={!!canUpload}
            onUploadField={onUploadField}
            uploading={uploading}
          />
        </Stack>
      )}

      {(hasChecklist || hasFormFields) && <Divider />}

      {hasRoutingActions && !pendingAction && (
        <Group spacing="sm">
          {routingActions.map((action, idx) => (
            <Button
              key={idx}
              size="sm"
              variant="filled"
              color={action.color || "blue"}
              loading={loading}
              onClick={() => handleApprove(idx)}
            >
              {routingActionLabel(action)}
            </Button>
          ))}
        </Group>
      )}

      {pendingAction ? (
        <Stack spacing="sm">
          <Textarea
            label={
              pendingAction === "terminate"
                ? "Termination reason"
                : "Rejection reason"
            }
            placeholder={
              pendingAction === "terminate"
                ? "Why is this request being terminated?"
                : "Why is this request being rejected?"
            }
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            minRows={2}
            autoFocus
          />
          <Group>
            <Button
              size="sm"
              color={pendingAction === "terminate" ? "red" : "orange"}
              variant="filled"
              onClick={
                pendingAction === "terminate" ? handleTerminate : handleReject
              }
              loading={loading}
            >
              {pendingAction === "terminate"
                ? "Confirm terminate"
                : "Confirm reject"}
            </Button>
            <Button size="sm" variant="default" onClick={cancelPendingAction}>{i18n.t("Cancel")}</Button>
          </Group>
        </Stack>
      ) : (
        <Group>
          <Button
            size="sm"
            color="green"
            variant="filled"
            onClick={() => handleApprove()}
            loading={loading}
          >
            Approve
          </Button>
          <Button
            size="sm"
            color="orange"
            variant="filled"
            onClick={() => setPendingAction("reject")}
          >
            Reject
          </Button>
          <Button
            size="sm"
            color="red"
            variant="filled"
            onClick={() => setPendingAction("terminate")}
          >{i18n.t("Terminate")}</Button>
        </Group>
      )}
    </Stack>
  );
};

export default McaApprovalForm;
